import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fleetService } from '../services/fleetService';
import { Search, CheckCircle, XCircle, Clock, Plus, Download, Car, DollarSign } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../utils/cn';
import ActionMenu from '../components/ActionMenu';
import AddDriverModal from '../components/AddDriverModal';

const statusConfig = {
  active: { label: 'Active', color: 'success', icon: CheckCircle },
  pending: { label: 'Pending', color: 'warning', icon: Clock },
  inactive: { label: 'Inactive', color: 'danger', icon: XCircle },
  offline: { label: 'Offline', color: 'danger', icon: XCircle },
  online: { label: 'Online', color: 'success', icon: CheckCircle },
  pending_verification: { label: 'Pending Verification', color: 'warning', icon: Clock },
};

const approvalStatusConfig = {
  pending: { label: 'Pending Approval', color: 'warning', icon: Clock },
  approved: { label: 'Approved', color: 'success', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'danger', icon: XCircle },
};

/** Account status from users.status (not online/offline) */
const accountStatusConfig = {
  active: { label: 'Active', color: 'success', icon: CheckCircle },
  inactive: { label: 'Inactive', color: 'danger', icon: XCircle },
  pending_verification: { label: 'Pending Verification', color: 'warning', icon: Clock },
  suspended: { label: 'Suspended', color: 'danger', icon: XCircle },
};

export default function Drivers() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [isDriverModalOpen, setIsDriverModalOpen] = useState(false);
  const [editDriverId, setEditDriverId] = useState(null);
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['fleet-drivers', statusFilter, page, search],
    queryFn: () => {
      return fleetService.getDrivers({
        status: statusFilter !== 'all' ? statusFilter : undefined,
        page,
        limit: 10,
        search: search || undefined,
      }).then((res) => res.data);
    },
  });

  const drivers = data?.data || [];
  const pagination = data?.pagination || {};
  
  // Client-side filtering for approval status if needed
  const filteredDrivers = drivers.filter((driver) => {
    if (statusFilter === 'pending') {
      return driver.approval_status === 'pending';
    }
    if (statusFilter === 'active') {
      return driver.status === 'active' && driver.approval_status === 'approved';
    }
    if (statusFilter === 'inactive') {
      return driver.status === 'inactive';
    }
    if (statusFilter === 'online') {
      return driver.is_online === true && driver.approval_status === 'approved';
    }
    if (statusFilter === 'offline') {
      return driver.is_online === false;
    }
    return true; // 'all' - show all drivers
  });

  const handleView = (driver) => {
    navigate(`/drivers/${driver.id}`);
  };

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-text dark:text-text-dark mb-2">Drivers</h1>
          <p className="text-text-secondary dark:text-text-secondary-dark">
            Manage your fleet drivers
          </p>
        </div>
        <div className="flex gap-2">
          <button className="hidden sm:flex items-center gap-2 px-4 py-2 bg-surface dark:bg-surface-dark border border-border dark:border-border-dark rounded-lg text-sm font-medium text-text dark:text-text-dark hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark transition-colors">
            <Download className="w-4 h-4" />
            Export
          </button>
          <button
            onClick={() => {
              setEditDriverId(null);
              setIsDriverModalOpen(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-all shadow-lg hover:shadow-xl"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Add Driver</span>
            <span className="sm:hidden">Add</span>
          </button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-surface dark:bg-surface-dark rounded-2xl border border-border dark:border-border-dark shadow-lg overflow-hidden">
        <div className="p-4 lg:p-6 border-b border-border dark:border-border-dark bg-gradient-to-r from-surface-secondary/50 to-transparent dark:from-surface-secondary-dark/50">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-tertiary dark:text-text-tertiary-dark" />
              <input
                type="text"
                placeholder="Search drivers by name, email, or phone..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-10 pr-4 py-3 bg-surface-secondary dark:bg-surface-secondary-dark border border-border dark:border-border-dark rounded-xl text-text dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {['all', 'active', 'inactive', 'online', 'offline', 'pending'].map((status) => {
                const config = statusConfig[status] || { label: 'All', color: 'primary' };
                return (
                  <button
                    key={status}
                    onClick={() => {
                      setStatusFilter(status);
                      setPage(1);
                    }}
                    className={cn(
                      'px-4 py-2 rounded-xl font-medium transition-all text-sm',
                      statusFilter === status
                        ? config.color === 'success'
                          ? 'bg-success text-white shadow-lg'
                          : config.color === 'warning'
                          ? 'bg-warning text-white shadow-lg'
                          : config.color === 'danger'
                          ? 'bg-danger text-white shadow-lg'
                          : 'bg-primary text-white shadow-lg'
                        : 'bg-surface-secondary dark:bg-surface-secondary-dark text-text-secondary dark:text-text-secondary-dark hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark hover:scale-105'
                    )}
                  >
                    {config.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="p-12 text-center">
            <div className="inline-block w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" />
            <div className="text-text-secondary dark:text-text-secondary-dark">Loading drivers...</div>
          </div>
        ) : filteredDrivers.length === 0 ? (
          <div className="p-12 text-center">
            <div className="text-text-secondary dark:text-text-secondary-dark mb-2">No drivers found</div>
            <button
              onClick={() => navigate('/drivers/new')}
              className="text-primary hover:text-primary-dark font-medium"
            >
              Add your first driver
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface-secondary dark:bg-surface-secondary-dark">
                  <tr>
                    <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider">
                      Driver
                    </th>
                    <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider hidden md:table-cell">
                      Email
                    </th>
                    <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider hidden lg:table-cell">
                      Phone
                    </th>
                    <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider">
                      Approval
                    </th>
                    <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider">
                      Account
                    </th>
                    <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider">
                      Online
                    </th>
                    <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider hidden lg:table-cell">
                      Vehicle
                    </th>
                    <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider hidden sm:table-cell">
                      Rating
                    </th>
                    <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider hidden md:table-cell">
                      Rides
                    </th>
                    <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider hidden xl:table-cell">
                      Earnings
                    </th>
                    <th className="px-4 lg:px-6 py-4 text-right text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border dark:divide-border-dark">
                  {filteredDrivers.map((driver) => {
                    // Determine online/offline status based on is_online field
                    const onlineStatus = driver.is_online ? 'online' : 'offline';
                    const onlineStatusConfig = statusConfig[onlineStatus] || statusConfig.offline;
                    const OnlineStatusIcon = onlineStatusConfig.icon;
                    
                    // Get approval status
                    const approvalStatus = driver.approval_status || 'pending';
                    const approvalConfig = approvalStatusConfig[approvalStatus] || approvalStatusConfig.pending;
                    const ApprovalIcon = approvalConfig.icon;

                    const accountKey = driver.status || 'active';
                    const accountCfg =
                      accountStatusConfig[accountKey] || {
                        label: accountKey.replace(/_/g, ' '),
                        color: 'warning',
                        icon: Clock,
                      };
                    const AccountIcon = accountCfg.icon;
                    
                    const fullName = `${driver.first_name || ''} ${driver.last_name || ''}`.trim() || 'N/A';
                    const initials = fullName !== 'N/A' 
                      ? `${driver.first_name?.charAt(0) || ''}${driver.last_name?.charAt(0) || ''}`.toUpperCase() || 'D'
                      : 'D';
                    
                    return (
                      <tr
                        key={driver.id}
                        className="hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark cursor-pointer transition-colors"
                        onClick={() => navigate(`/drivers/${driver.id}`)}
                      >
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary-dark rounded-full flex items-center justify-center text-white font-semibold shadow-lg">
                              {initials}
                            </div>
                            <div>
                              <div className="text-sm font-semibold text-text dark:text-text-dark">
                                {fullName}
                              </div>
                              <div className="text-xs text-text-secondary dark:text-text-secondary-dark md:hidden">
                                {driver.email}
                              </div>
                              {driver.driver_license_number && (
                                <div className="text-xs text-text-tertiary dark:text-text-tertiary-dark md:hidden mt-0.5">
                                  License: {driver.driver_license_number}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-text-secondary dark:text-text-secondary-dark hidden md:table-cell">
                          {driver.email}
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-text-secondary dark:text-text-secondary-dark hidden lg:table-cell">
                          {driver.phone || 'N/A'}
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                          <span
                            className={cn(
                              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold',
                              approvalConfig.color === 'success'
                                ? 'bg-success-light dark:bg-success-dark text-success'
                                : approvalConfig.color === 'warning'
                                ? 'bg-warning-light dark:bg-warning-dark text-warning'
                                : approvalConfig.color === 'danger'
                                ? 'bg-danger-light dark:bg-danger-dark text-danger'
                                : 'bg-primary-light dark:bg-primary-dark text-primary'
                            )}
                            title={`Approval Status: ${approvalConfig.label}`}
                          >
                            <ApprovalIcon className="w-3 h-3" />
                            <span className="hidden sm:inline">{approvalConfig.label}</span>
                            <span className="sm:hidden">{approvalConfig.label.split(' ')[0]}</span>
                          </span>
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                          <span
                            className={cn(
                              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold',
                              accountCfg.color === 'success'
                                ? 'bg-success-light dark:bg-success-dark text-success'
                                : accountCfg.color === 'warning'
                                ? 'bg-warning-light dark:bg-warning-dark text-warning'
                                : accountCfg.color === 'danger'
                                ? 'bg-danger-light dark:bg-danger-dark text-danger'
                                : 'bg-primary-light dark:bg-primary-dark text-primary'
                            )}
                            title={`Account: ${accountCfg.label}`}
                          >
                            <AccountIcon className="w-3 h-3" />
                            <span className="hidden sm:inline">{accountCfg.label}</span>
                            <span className="sm:hidden">{accountCfg.label.split(' ')[0]}</span>
                          </span>
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                          <span
                            className={cn(
                              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold',
                              onlineStatusConfig.color === 'success'
                                ? 'bg-success-light dark:bg-success-dark text-success'
                                : onlineStatusConfig.color === 'danger'
                                ? 'bg-danger-light dark:bg-danger-dark text-danger'
                                : 'bg-primary-light dark:bg-primary-dark text-primary'
                            )}
                            title={`Online: ${onlineStatusConfig.label}`}
                          >
                            <OnlineStatusIcon className="w-3 h-3" />
                            {onlineStatusConfig.label}
                          </span>
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-text dark:text-text-dark hidden lg:table-cell">
                          {driver.vehicle ? (
                            <div className="flex items-center gap-1.5">
                              <Car className="w-4 h-4 text-text-tertiary dark:text-text-tertiary-dark" />
                              <span className="font-mono text-xs">
                                {driver.vehicle.license_plate || driver.vehicle.licensePlate || 'N/A'}
                              </span>
                            </div>
                          ) : (
                            <span className="text-text-tertiary dark:text-text-tertiary-dark text-xs">Not Assigned</span>
                          )}
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-text dark:text-text-dark hidden sm:table-cell">
                          {driver.driver_rating ? (
                            <span className="font-semibold">{driver.driver_rating.toFixed(1)} ⭐</span>
                          ) : (
                            <span className="text-text-tertiary dark:text-text-tertiary-dark">N/A</span>
                          )}
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-text dark:text-text-dark hidden md:table-cell">
                          <span className="font-semibold">{driver.total_rides || 0}</span>
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-text dark:text-text-dark hidden xl:table-cell">
                          {driver.totalEarnings !== undefined ? (
                            <div className="flex items-center gap-1">
                              <DollarSign className="w-3.5 h-3.5 text-success" />
                              <span className="font-semibold">NOK {(driver.totalEarnings || 0).toFixed(2)}</span>
                            </div>
                          ) : (
                            <span className="text-text-tertiary dark:text-text-tertiary-dark">NOK 0.00</span>
                          )}
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-right text-sm font-medium relative">
                          <div onClick={(e) => e.stopPropagation()} className="relative z-10">
                            <ActionMenu
                              onView={() => handleView(driver)}
                              onEdit={() => {
                                setEditDriverId(driver.id);
                                setIsDriverModalOpen(true);
                              }}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.total > 0 && (
              <div className="p-4 lg:p-6 border-t border-border dark:border-border-dark flex flex-col sm:flex-row items-center justify-between gap-4 bg-surface-secondary/30 dark:bg-surface-secondary-dark/30">
                <div className="text-sm text-text-secondary dark:text-text-secondary-dark">
                  Showing <span className="font-semibold text-text dark:text-text-dark">{filteredDrivers.length}</span> of{' '}
                  <span className="font-semibold text-text dark:text-text-dark">{pagination.total || 0}</span> drivers
                  {statusFilter !== 'all' && (
                    <span className="ml-2 text-text-tertiary dark:text-text-tertiary-dark">
                      (filtered)
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 bg-surface dark:bg-surface-dark border border-border dark:border-border-dark rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark transition-colors text-sm font-medium"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={!pagination.pages || page >= pagination.pages}
                    className="px-4 py-2 bg-primary text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary-dark transition-colors text-sm font-medium shadow-lg"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add Driver Modal */}
      <AddDriverModal
        isOpen={isDriverModalOpen}
        onClose={() => {
          setIsDriverModalOpen(false);
          setEditDriverId(null);
        }}
        mode={editDriverId ? 'edit' : 'create'}
        driverId={editDriverId}
      />
    </div>
  );
}
